/**
 * Canvas2D overlay: measuring geometry with its labels, and the brush cursor. Everything is
 * sampled in world coordinates and pushed through view → model → screen, so geodesics and
 * circles come out right in every model.
 */
import type { Complex } from '../geometry/complex';
import {
  idealEndpoints,
  sampleCircle,
  sampleFullGeodesic,
  sampleGeodesic,
} from '../geometry/hyperbolic';
import { type Isometry, apply } from '../geometry/mobius';
import { type ModelId, fromDisk } from '../geometry/models';
import { type Camera, modelToScreen } from '../render/camera';
import { type Measurement, fmt, fmtDeg } from './measure';

export interface OverlayFrame {
  camera: Camera;
  model: ModelId;
  view: Isometry;
  /** Hide everything while the model morphs. */
  hidden: boolean;
}

export interface Cursor {
  x: number;
  y: number;
  /** Brush radius on screen (CSS px); 0 hides the brush ring. */
  radius: number;
  eraser: boolean;
  color: string;
}

const INK = '#19214d';
const VERMILION = '#c23d22';
const IVORY = '#fbf6e9';
const GOLD = '#e2c15a';

type XY = { x: number; y: number };

export class OverlayRenderer {
  readonly canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private dpr = 1;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D is not available');
    this.ctx = ctx;
  }

  resize(width: number, height: number, dpr: number): void {
    this.dpr = dpr;
    const w = Math.round(width * dpr);
    const h = Math.round(height * dpr);
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
  }

  /** Screen position of a world point, or null if it is (numerically) at infinity. */
  static project(f: OverlayFrame, w: Complex): XY | null {
    const z = apply(f.view, w);
    const r2 = z.re * z.re + z.im * z.im;
    if (!(r2 < 1)) return null;
    const m = fromDisk(f.model, z);
    if (!Number.isFinite(m.re) || !Number.isFinite(m.im)) return null;
    return modelToScreen(f.camera, m);
  }

  draw(f: OverlayFrame, m: Measurement | null, cursor: Cursor | null, penAt: XY | null): void {
    const ctx = this.ctx;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.clearRect(0, 0, f.camera.width, f.camera.height);
    if (!f.hidden && m && m.points.length > 0) drawMeasurement(ctx, f, m);
    if (penAt) drawPen(ctx, penAt);
    if (cursor && cursor.radius > 0) drawCursor(ctx, cursor);
  }
}

function polyline(ctx: CanvasRenderingContext2D, f: OverlayFrame, pts: Complex[], close = false) {
  ctx.beginPath();
  let pen = false;
  let prev: XY | null = null;
  for (const p of pts) {
    const s = OverlayRenderer.project(f, p);
    if (!s) {
      pen = false;
      prev = null;
      continue;
    }
    // break the line if consecutive samples jump far (the band and half-plane are unbounded)
    if (prev && Math.hypot(s.x - prev.x, s.y - prev.y) > 4 * (f.camera.width + f.camera.height)) {
      pen = false;
    }
    if (pen) ctx.lineTo(s.x, s.y);
    else ctx.moveTo(s.x, s.y);
    pen = true;
    prev = s;
  }
  if (close) ctx.closePath();
}

function strokeTwice(ctx: CanvasRenderingContext2D, color: string, width: number) {
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.strokeStyle = 'rgba(251, 246, 233, 0.92)';
  ctx.lineWidth = width + 3.5;
  ctx.stroke();
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.stroke();
}

function handle(ctx: CanvasRenderingContext2D, s: XY, label?: string) {
  ctx.beginPath();
  ctx.arc(s.x, s.y, 7, 0, 2 * Math.PI);
  ctx.fillStyle = IVORY;
  ctx.fill();
  ctx.lineWidth = 2.5;
  ctx.strokeStyle = INK;
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(s.x, s.y, 2.6, 0, 2 * Math.PI);
  ctx.fillStyle = VERMILION;
  ctx.fill();
  if (label) tag(ctx, s.x + 12, s.y - 12, label, 'left');
}

/** A label with a parchment halo, readable over any part of the pattern. */
function tag(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  text: string,
  align: CanvasTextAlign = 'center',
) {
  ctx.font = '600 14px "Jost Variable", Jost, system-ui, sans-serif';
  ctx.textAlign = align;
  ctx.textBaseline = 'middle';
  const w = ctx.measureText(text).width;
  const pad = 6;
  const x0 = align === 'left' ? x - pad : align === 'right' ? x - w - pad : x - w / 2 - pad;
  ctx.fillStyle = 'rgba(20, 26, 58, 0.88)';
  roundRect(ctx, x0, y - 12, w + 2 * pad, 24, 7);
  ctx.fill();
  ctx.fillStyle = IVORY;
  ctx.fillText(text, x, y + 0.5);
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function drawMeasurement(ctx: CanvasRenderingContext2D, f: OverlayFrame, m: Measurement) {
  const pts = m.points;
  const screen = pts.map((p) => OverlayRenderer.project(f, p));
  const ro = m.readout();
  ctx.save();
  if (m.kind === 'geodesic' || m.kind === 'triangle') {
    // complete geodesics, faint, to show they meet the boundary at right angles
    const pairs: [Complex, Complex][] =
      m.kind === 'geodesic'
        ? pts.length === 2
          ? [[pts[0], pts[1]]]
          : []
        : pts.length === 3
          ? [
              [pts[0], pts[1]],
              [pts[1], pts[2]],
              [pts[2], pts[0]],
            ]
          : pts.length === 2
            ? [[pts[0], pts[1]]]
            : [];
    if (m.kind === 'geodesic' && pairs.length === 1) {
      polyline(ctx, f, sampleFullGeodesic(pairs[0][0], pairs[0][1], 18, 400));
      ctx.setLineDash([2, 6]);
      ctx.lineCap = 'round';
      ctx.strokeStyle = 'rgba(25, 33, 77, 0.75)';
      ctx.lineWidth = 1.6;
      ctx.stroke();
      ctx.setLineDash([]);
      for (const e of idealEndpoints(pairs[0][0], pairs[0][1])) {
        const s = OverlayRenderer.project(f, { re: e.re * 0.999999, im: e.im * 0.999999 });
        if (!s) continue;
        ctx.beginPath();
        ctx.arc(s.x, s.y, 3.5, 0, 2 * Math.PI);
        ctx.fillStyle = GOLD;
        ctx.fill();
      }
    }
    if (m.kind === 'triangle' && pts.length === 3) {
      polyline(ctx, f, [
        ...sampleGeodesic(pts[0], pts[1], 96),
        ...sampleGeodesic(pts[1], pts[2], 96),
        ...sampleGeodesic(pts[2], pts[0], 96),
      ]);
      ctx.fillStyle = 'rgba(201, 162, 39, 0.2)';
      ctx.fill();
    }
    for (const [a, b] of pairs) {
      polyline(ctx, f, sampleGeodesic(a, b, 160));
      strokeTwice(ctx, INK, 2.6);
    }
  }
  if (m.kind === 'circle' && pts.length >= 1) {
    if (pts.length === 2 && ro && ro.kind === 'circle') {
      polyline(ctx, f, sampleCircle(pts[0], ro.radius, 240), true);
      ctx.fillStyle = 'rgba(201, 162, 39, 0.16)';
      ctx.fill();
      strokeTwice(ctx, INK, 2.6);
      polyline(ctx, f, sampleGeodesic(pts[0], pts[1], 80));
      ctx.setLineDash([5, 5]);
      ctx.strokeStyle = INK;
      ctx.lineWidth = 1.8;
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }
  // labels
  if (ro?.kind === 'geodesic' && screen[0] && screen[1]) {
    const mid = OverlayRenderer.project(f, sampleGeodesic(pts[0], pts[1], 2)[1]);
    if (mid) tag(ctx, mid.x, mid.y - 20, `d = ${fmt(ro.distance)}`);
  }
  if (ro?.kind === 'triangle') {
    const names = ['α', 'β', 'γ'];
    screen.forEach((s, i) => {
      if (!s) return;
      // push the angle label away from the triangle's centroid
      const others = screen.filter((_, j) => j !== i).filter((v): v is XY => !!v);
      const cx = others.reduce((a, v) => a + v.x, s.x) / (others.length + 1);
      const cy = others.reduce((a, v) => a + v.y, s.y) / (others.length + 1);
      const dx = s.x - cx;
      const dy = s.y - cy;
      const d = Math.hypot(dx, dy) || 1;
      tag(ctx, s.x + (dx / d) * 30, s.y + (dy / d) * 30, `${names[i]} ${fmtDeg(ro.angles[i])}`);
    });
  }
  if (ro?.kind === 'circle' && screen[0]) {
    const edge = OverlayRenderer.project(f, pts[1]);
    if (edge) {
      const mx = (screen[0].x + edge.x) / 2;
      const my = (screen[0].y + edge.y) / 2;
      tag(ctx, mx, my - 18, `r = ${fmt(ro.radius)}`);
    }
  }
  screen.forEach((s) => s && handle(ctx, s));
  ctx.restore();
}

function drawPen(ctx: CanvasRenderingContext2D, s: XY) {
  ctx.save();
  ctx.beginPath();
  ctx.arc(s.x, s.y, 9, 0, 2 * Math.PI);
  ctx.strokeStyle = INK;
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(s.x - 15, s.y);
  ctx.lineTo(s.x - 11, s.y);
  ctx.moveTo(s.x + 11, s.y);
  ctx.lineTo(s.x + 15, s.y);
  ctx.moveTo(s.x, s.y - 15);
  ctx.lineTo(s.x, s.y - 11);
  ctx.moveTo(s.x, s.y + 11);
  ctx.lineTo(s.x, s.y + 15);
  ctx.stroke();
  ctx.restore();
}

function drawCursor(ctx: CanvasRenderingContext2D, c: Cursor) {
  ctx.save();
  ctx.beginPath();
  ctx.arc(c.x, c.y, Math.max(c.radius, 2), 0, 2 * Math.PI);
  if (!c.eraser) {
    ctx.fillStyle = c.color;
    ctx.globalAlpha = 0.35;
    ctx.fill();
    ctx.globalAlpha = 1;
  }
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = 'rgba(251, 246, 233, 0.95)';
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(c.x, c.y, Math.max(c.radius, 2) + 1.5, 0, 2 * Math.PI);
  ctx.strokeStyle = 'rgba(20, 26, 58, 0.9)';
  ctx.lineWidth = 1;
  if (c.eraser) ctx.setLineDash([3, 3]);
  ctx.stroke();
  ctx.restore();
}
